import { useEffect, useRef, useState } from 'react';
import { TextInput } from '@carbon/react';

// A numeric text field that fixes the clunky Carbon NumberInput editing experience.
//
// Carbon's NumberInput renders a native <input type="number">, which silently DROPS
// in-progress values the browser can't yet parse as a number — a lone "-", a lone ".",
// or an empty box all read back as "". So typing "-" first (to build "-2") collapses to 0,
// and you can never lead with a minus. This wrapper keeps the raw text while the field is
// focused and only coerces to a number when the value is actually parseable (and again,
// normalized/clamped, on blur), so leading "-", trailing ".", and transient empties all work.
//
// It intentionally uses a text input (inputMode="decimal"/"numeric") rather than
// type="number" — that is the only reliable way to preserve those intermediate states.

interface NumericFieldProps {
  id: string;
  value: number;
  onValueChange: (value: number) => void;
  label?: string;
  hideLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  min?: number;
  max?: number;
  allowNegative?: boolean; // default true
  allowDecimal?: boolean; // default true
  disabled?: boolean;
  invalid?: boolean;
  invalidText?: string;
  className?: string;
  placeholder?: string;
  selectOnFocus?: boolean; // default true — overtype the whole value with a single click
}

const format = (n: number) => (Number.isFinite(n) ? String(n) : '');

// A finished, standalone number — excludes the in-progress fragments ("", "-", ".", "-.").
const isComplete = (text: string) =>
  text !== '' && Number.isFinite(Number(text)) && !/[.-]$/.test(text);

export function NumericField({
  id,
  value,
  onValueChange,
  label = '',
  hideLabel,
  size,
  min,
  max,
  allowNegative = true,
  allowDecimal = true,
  disabled,
  invalid,
  invalidText,
  className,
  placeholder,
  selectOnFocus = true,
}: NumericFieldProps) {
  const [text, setText] = useState(() => format(value));
  const focused = useRef(false);

  // Reflect external value changes only while the user isn't mid-edit, so our
  // in-progress text is never clobbered by the parent re-rendering with the coerced number.
  useEffect(() => {
    if (!focused.current) setText(format(value));
  }, [value]);

  // Permissive "still typing a number" matcher — allows the transient fragments above.
  const pattern = new RegExp(
    `^${allowNegative ? '-?' : ''}\\d*${allowDecimal ? '\\.?\\d*' : ''}$`
  );

  const clamp = (n: number) => {
    let out = allowDecimal ? n : Math.trunc(n);
    if (min !== undefined) out = Math.max(min, out);
    if (max !== undefined) out = Math.min(max, out);
    return out;
  };

  const handleChange = (raw: string) => {
    if (!pattern.test(raw)) return; // reject letters/extra signs; keep prior text
    setText(raw);
    if (isComplete(raw)) onValueChange(clamp(Number(raw)));
  };

  const handleBlur = () => {
    focused.current = false;
    // Commit leniently: a trailing-dot ("2.") or leading-dot (".5") is a finished number
    // once focus leaves; only truly non-numeric text ("", "-", ".") falls back.
    const parsed = Number(text);
    const normalized =
      text !== '' && Number.isFinite(parsed) ? clamp(parsed) : (min ?? 0);
    onValueChange(normalized);
    setText(format(normalized)); // snap the display to the committed, clamped value
  };

  return (
    <TextInput
      id={id}
      labelText={label}
      hideLabel={hideLabel}
      size={size}
      value={text}
      disabled={disabled}
      invalid={invalid}
      invalidText={invalidText}
      className={className}
      placeholder={placeholder}
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      onFocus={(e) => {
        focused.current = true;
        if (selectOnFocus) e.target.select();
      }}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
    />
  );
}
